import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../../../Firebase";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { V3Room, V3Event, V3User, V3Submission, V3Problem } from "../types/v3types";

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow: hidden;
  }
`;

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.05); }
`;

const BackgroundAnimation = styled.div`
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: ${pulse} 4s ease-in-out infinite;
`;

const Content = styled.div`
  width: 95%;
  height: 90%;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
`;

const ProblemSection = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
`;

const RankingSection = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const ProblemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const ProblemTitle = styled.h1`
  font-size: 36px;
  color: #333;
`;

const Timer = styled.div`
  font-size: 48px;
  font-weight: bold;
  color: #dc143c;
`;

const ProblemImage = styled.img`
  width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: 10px;
  margin: 20px 0;
`;

const RankingTitle = styled.h2`
  font-size: 28px;
  color: #dc143c;
  margin-bottom: 20px;
  text-align: center;
`;

const RankingList = styled.div`
  height: calc(100% - 60px);
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
`;

const RankingItem = styled.div<{ rank: number }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  background: ${props => {
    if (props.rank === 1) return "linear-gradient(135deg, #ffd700, #ffed4b)";
    if (props.rank === 2) return "linear-gradient(135deg, #c0c0c0, #e8e8e8)";
    if (props.rank === 3) return "linear-gradient(135deg, #cd7f32, #e6a857)";
    return "#f8f9fa";
  }};
  border-radius: 8px;
  box-shadow: ${props => props.rank <= 3 ? "0 2px 8px rgba(0,0,0,0.15)" : "0 1px 3px rgba(0,0,0,0.1)"};
`;

const RankNumber = styled.span<{ rank: number }>`
  font-size: ${props => props.rank <= 3 ? "24px" : "20px"};
  font-weight: bold;
  color: ${props => props.rank <= 3 ? "#fff" : "#333"};
  width: 40px;
  text-align: center;
`;

const RankName = styled.span`
  flex: 1;
  font-size: 18px;
  margin-left: 12px;
  color: ${props => props.color || "#333"};
`;

const RankScore = styled.span`
  font-size: 20px;
  font-weight: bold;
  color: ${props => props.color || "#dc143c"};
`;

const WaitingScreen = styled.div`
  text-align: center;
  color: #333;
`;

const Title = styled.h1`
  font-size: 48px;
  margin-bottom: 20px;
  color: #dc143c;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  color: #666;
  margin-bottom: 40px;
`;

const CountdownDisplay = styled.div`
  font-size: 32px;
  color: #dc143c;
  font-weight: bold;
  text-align: center;
  margin-bottom: 20px;
`;

const LockedMessage = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
  font-size: 24px;
`;

const V3KachidokiScreen: React.FC = () => {
  const { projectId, roomId } = useParams<{ projectId: string; roomId: string }>();
  const [room, setRoom] = useState<V3Room | null>(null);
  const [event, setEvent] = useState<V3Event | null>(null);
  const [participants, setParticipants] = useState<V3User[]>([]);
  const [submissions, setSubmissions] = useState<V3Submission[]>([]);
  const [currentProblem, setCurrentProblem] = useState<V3Problem | null>(null);
  const [nextReleaseIn, setNextReleaseIn] = useState<number>(0);

  useEffect(() => {
    if (!roomId || !projectId) return;

    // Subscribe to room updates
    const unsubscribeRoom = onSnapshot(
      doc(db, "v3rooms", roomId),
      (doc) => {
        if (doc.exists()) {
          setRoom(doc.data() as V3Room);
        }
      }
    );

    return () => {
      unsubscribeRoom();
    };
  }, [roomId, projectId]);

  useEffect(() => {
    if (!room) return;

    // Subscribe to event
    const unsubscribeEvent = onSnapshot(
      doc(db, "v3events", room.eventId),
      (doc) => {
        if (doc.exists()) {
          const eventData = doc.data() as V3Event;
          setEvent(eventData);
          
          // Set current problem
          if (room.currentProblemIndex >= 0 && eventData.problems[room.currentProblemIndex]) {
            setCurrentProblem(eventData.problems[room.currentProblemIndex]);
          }
        }
      }
    );

    // Subscribe to participants
    const participantIds = room.participants || [];
    if (participantIds.length > 0) {
      const unsubscribes = participantIds.map((userId) =>
        onSnapshot(doc(db, "v3users", userId), (doc) => {
          if (doc.exists()) {
            const userData = doc.data() as V3User;
            setParticipants((prev) => {
              const filtered = prev.filter((u) => u.userId !== userData.userId);
              return [...filtered, userData];
            });
          }
        })
      );

      return () => {
        unsubscribeEvent();
        unsubscribes.forEach((unsub) => unsub());
      };
    }

    return () => {
      unsubscribeEvent();
    };
  }, [room]);

  useEffect(() => {
    if (!roomId) return;

    // Subscribe to submissions
    const q = query(
      collection(db, "v3submissions"),
      where("roomId", "==", roomId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const subs: V3Submission[] = [];
      snapshot.forEach((doc) => {
        subs.push(doc.data() as V3Submission);
      });
      setSubmissions(subs);
    });

    return () => unsubscribe();
  }, [roomId]);

  useEffect(() => {
    // Calculate countdown to next problem release
    if (!room || !event || !room.kachidokiStatus?.isRunning) {
      setNextReleaseIn(0);
      return;
    }

    const interval = setInterval(() => {
      if (room.kachidokiStatus?.nextReleaseTime) {
        const now = Date.now();
        const releaseTime = room.kachidokiStatus.nextReleaseTime.toMillis();
        const remaining = Math.max(0, Math.ceil((releaseTime - now) / 1000));
        setNextReleaseIn(remaining);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [room, event]);

  const calculateScore = (userId: string) => {
    return submissions
      .filter(sub => sub.userId === userId && sub.isCorrect)
      .reduce((total, sub) => total + sub.score + (sub.kachidokiPoints || 0), 0);
  };

  const getRanking = () => {
    return participants
      .map(user => ({
        user,
        score: calculateScore(user.userId)
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 10); // Top 10
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!room || !event) {
    return (
      <Container>
        <GlobalStyle />
        <BackgroundAnimation />
        <Content>
          <WaitingScreen>Loading...</WaitingScreen>
        </Content>
      </Container>
    );
  }

  const isCurrentProblemReleased = room.kachidokiStatus?.releasedProblems?.includes(room.currentProblemIndex);

  return (
    <Container>
      <GlobalStyle />
      <BackgroundAnimation />
      <Content>
        <ProblemSection>
          {room.status === "waiting" ? (
            <WaitingScreen>
              <Title>{event.eventName}</Title>
              <SubTitle>{room.roomName}</SubTitle>
              <SubTitle style={{ marginTop: 60 }}>
                参加者: {participants.length}人
              </SubTitle>
            </WaitingScreen>
          ) : room.status === "finished" ? (
            <WaitingScreen>
              <Title>競技終了</Title>
              <SubTitle>お疲れさまでした！</SubTitle>
            </WaitingScreen>
          ) : currentProblem && isCurrentProblemReleased ? (
            <>
              <ProblemHeader>
                <ProblemTitle>問題 {room.currentProblemIndex + 1}</ProblemTitle>
                {room.kachidokiStatus?.isRunning && nextReleaseIn > 0 && (
                  <Timer>{formatTime(nextReleaseIn)}</Timer>
                )}
              </ProblemHeader>
              
              {currentProblem.imageUrls && currentProblem.imageUrls[0] && (
                <ProblemImage src={currentProblem.imageUrls[0]} alt="Problem" />
              )}
              
              {room.kachidokiStatus?.isRunning && nextReleaseIn > 0 && (
                <CountdownDisplay>
                  次の問題まで: {formatTime(nextReleaseIn)}
                </CountdownDisplay>
              )}
            </>
          ) : (
            <LockedMessage>
              問題準備中...
            </LockedMessage>
          )}
        </ProblemSection>

        <RankingSection>
          <RankingTitle>ランキング</RankingTitle>
          <RankingList>
            {getRanking().map((item, index) => (
              <RankingItem key={item.user.userId} rank={index + 1}>
                <RankNumber rank={index + 1}>{index + 1}</RankNumber>
                <RankName color={index < 3 ? "#fff" : undefined}>
                  {item.user.username}
                </RankName>
                <RankScore color={index < 3 ? "#fff" : undefined}>
                  {item.score}点
                </RankScore>
              </RankingItem>
            ))}
          </RankingList>
        </RankingSection>
      </Content>
    </Container>
  );
};

export default V3KachidokiScreen;